import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { View, Text, FlatList, TextInput, Image } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { CreateResponsiveStyle, DEVICE_SIZES } from "rn-responsive-styles";
import { toast } from "react-toastify";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { CommonButton } from "../../../../common/CommonButton";
import {
  CancelEventMessage,
  MessageList,
  NoMessages,
} from "../../../../common/CommonChatMessageLIst";
import { MessageInput } from "../../../../common/CommonMessageInput";
import { ShowLoader } from "../../../../common/ShowLoader";
import { AppColors } from "../../../../utility/AppColors";
import withErrorBoundary from "../../../../utility/auth/withErrorBoundary";
import { useLiveStream } from "../../../../context/LiveStreamContext";
import { AppImages } from "../../../../utility/AppImages";
import StreamActionIcon from "../../../../Components/StreamActionIcons";

interface EventChatComponentProps {
  deviceSize: DEVICE_SIZES;
  onChatTap: () => void;
  chatID: string;
  title: string;
  isSignIn?: boolean;
  fetchChatHistoryTrigger: any;
  resetFetchChatHistoryTrigger: any;
  IsEventCanceled?: boolean;
  reason?: string;
}

const EventChatComponent: React.FC<EventChatComponentProps> = ({
  deviceSize,
  onChatTap,
  chatID,
  title,
  isSignIn,
  fetchChatHistoryTrigger,
  resetFetchChatHistoryTrigger,
  IsEventCanceled = false,
  reason = "",
}) => {
  const styles = useStyles();
  const { audienceData, sendComment, userData, currentEventId } =
    useLiveStream();
  const flatListRef = useRef<FlatList | null>(null);
  const inputRef = useRef<TextInput | null>(null);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [newMessage, setNewMessage] = useState("");
  const [pendingMessages, setPendingMessages] = useState<any[]>([]);

  // Memoize audience chat data, filtering for Audience comments
  const audienceChatData = useMemo(() => {
    const chats = audienceData?.chats || [];
    return chats
      .filter((chat: any) => chat.commentType === "Audience")
      .sort((a: any, b: any) => a.timestamp - b.timestamp);
  }, [audienceData?.chats]);

  // Create a set of comment IDs to track server-confirmed messages
  const audienceCommentIds = useMemo(() => {
    return new Set((audienceChatData || []).map((c: any) => c.commentId));
  }, [audienceChatData]);

  // Combine audience chat data with pending messages
  const streamChatData = useMemo(() => {
    const combined = [...audienceChatData, ...pendingMessages];
    const byId = new Map<string, any>();
    combined.forEach((m) => {
      if (!byId.has(m.commentId)) byId.set(m.commentId, m);
    });
    return Array.from(byId.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [audienceChatData, pendingMessages]);

  // Drop pending messages once they are echoed back by the server
  useEffect(() => {
    if (pendingMessages.length === 0) return;
    setPendingMessages((prev) =>
      prev.filter((m) => !audienceCommentIds.has(m.commentId))
    );
  }, [audienceCommentIds]);

  // Handle chat history fetching or data reset when chatID or userData changes
  useEffect(() => {
    if (chatID && userData) {
      setShowLoader(true);
      // Reset chat data when chatID changes
      setPendingMessages([]);
      setTimeout(() => setShowLoader(false), 1000); // Simulate loading
    }
  }, [chatID, userData]);

  // Handle fetchChatHistoryTrigger
  useEffect(() => {
    if (fetchChatHistoryTrigger) {
      setPendingMessages([]);
      setShowLoader(true);
      setTimeout(() => {
        setShowLoader(false);
        resetFetchChatHistoryTrigger();
      }, 1000); // Simulate refetch
    }
  }, [fetchChatHistoryTrigger, resetFetchChatHistoryTrigger]);

  // Auto-scroll to the latest message
  useEffect(() => {
    if (!flatListRef.current || streamChatData.length === 0) return;
    const scrollTimer = setTimeout(() => {
      try {
        flatListRef.current?.scrollToIndex({
          animated: true,
          index: streamChatData.length - 1,
        });
      } catch (e) {}
    }, 100);
    return () => clearTimeout(scrollTimer);
  }, [streamChatData.length]);

  // Handle sending a message
  const handleSendMessage = useCallback(() => {
    if (!newMessage.trim()) {
      toast.warning("Please enter a message");
      return;
    }

    if (!userData?.userID || !chatID || isSignIn) {
      toast.warning("Please sign up to send a message");
      return;
    }

    const newComment = {
      userId: userData.userID,
      eventId: chatID,
      commentData: newMessage,
      commentType: "Audience",
      timestamp: Date.now(),
      profilePicture: userData?.profilePicture || "",
      commentId: `${userData.userID}-${Date.now()}`,
    };

    setPendingMessages((prev) => [...prev, newComment]);
    sendComment(userData.userID, chatID, newMessage, "Audience");
    setNewMessage("");

    // Scroll to latest message and refocus input
    setTimeout(() => {
      flatListRef.current?.scrollToEnd({ animated: true });
      inputRef.current?.focus();
    }, 100);
  }, [newMessage, userData, chatID, isSignIn, sendComment]);

  return (
    <View style={styles.chatBox}>
      <View style={[styles.eventView, { width: 500 }]}>
        <Text
          style={[
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
              ? AppFontStyle.BOLD_ANYBODY_24
              : AppFontStyle.REGULAR_ANYBODY_18,
            { marginHorizontal: 5 },
          ]}
        >
          {title ? title : "Chat"}
        </Text>
        <View style={styles.separator} />
        <LinearGradient
          colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
          start={[0, 0]}
          end={[1, 1]}
          style={styles.chatOuterView}
        >
          <CommonButton
            title="Chat"
            onTap={onChatTap}
            buttonStyle={styles.btnChat}
            textStyle={AppFontStyle.REGULAR_ANYBODY_12}
          />
          <View style={styles.separator} />
          <View style={styles.messageListContainer}>
            {IsEventCanceled ? (
              <CancelEventMessage
                deviceSize={deviceSize}
                pageType="event"
                isSignIn={isSignIn}
                reason={reason}
              />
            ) : streamChatData.length !== 0 && !isSignIn ? (
              <FlatList
                ref={flatListRef}
                data={streamChatData}
                keyExtractor={(item) => item.commentId}
                showsVerticalScrollIndicator={false}
                renderItem={({ item }) => (
                  <View
                    style={{
                      flexDirection: "row",
                      marginVertical: 8,
                      columnGap: 10,
                    }}
                  >
                    <Image
                      source={
                        item.profilePicture
                          ? { uri: item.profilePicture }
                          : AppImages.Ellipse1
                      }
                      style={{
                        width: 27,
                        height: 27,
                        borderRadius: 100,
                      }}
                    />
                    <View
                      style={{ flex: 1, flexDirection: "row", columnGap: 5 }}
                    >
                      <Text
                        style={[
                          deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                            ? AppFontStyle.REGULAR_ANYBODY_16
                            : AppFontStyle.REGULAR_ANYBODY_14,
                        ]}
                      >
                        {item.userId}:
                      </Text>
                      <Text
                        style={[
                          AppFontStyle.REGULAR_ANYBODY_12,
                          { opacity: 0.5, lineHeight: 17 },
                        ]}
                      >
                        {item.commentData}
                      </Text>
                    </View>
                  </View>
                )}
              />
            ) : (
              <NoMessages
                deviceSize={deviceSize}
                pageType="event"
                isSignIn={isSignIn}
                zeroMessage={streamChatData.length === 0}
              />
            )}
          </View>
          <View style={styles.separator} />
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <View
              style={{
                flex: 1,
                height: 44,
                position: "relative",
              }}
            >
              <TextInput
                ref={inputRef}
                style={{
                  flex: 1,
                  height: 44,
                  paddingVertical: 0,
                  paddingHorizontal: 16,
                  borderRadius: 8,
                  paddingRight: 50,
                  backgroundColor: "#1F2937",
                  fontSize: 14,
                  borderWidth: 0.8,
                  borderColor: "#374151",
                  color: "#FFFFFF",
                }}
                placeholderTextColor="#999999"
                placeholder="Write Here"
                value={newMessage}
                onChangeText={setNewMessage}
                onSubmitEditing={handleSendMessage}
                blurOnSubmit={false}
                multiline={false}
              />
              <View
                style={{
                  position: "absolute",
                  right: 2,
                  top: 2,
                  bottom: 2,
                  opacity: isSignIn ? 0.5 : 1,
                }}
              >
                <StreamActionIcon
                  type="send"
                  onPress={isSignIn ? undefined : handleSendMessage}
                />
              </View>
            </View>
          </View>
        </LinearGradient>
        <ShowLoader showIndicater={showLoader} />
      </View>
    </View>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    eventView: {
      marginBottom: 5,
      alignSelf: "flex-end",
    },
    separator: {
      height: 1,
      backgroundColor: AppColors.grayColor_484848,
      marginVertical: 10,
    },
    messageListContainer: {
      minHeight: 350,
      maxHeight: 350,
      minWidth: 350,
      marginVertical: "5%",
    },
    chatBox: {
      marginTop: 20,
      position: "absolute",
      right: 0,
    },
    chatOuterView: {
      margin: 5,
      borderRadius: 8,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      borderWidth: 0.8,
      borderColor: "#374151",
      paddingHorizontal: 10,
      marginVertical: 10,
    },
    btnChat: {
      paddingVertical: 0,
      paddingHorizontal: 15,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      shadowOpacity: 0,
      height: 40,
      marginTop: 10,
      borderRadius: 7,
    },
  },
  {
    [DEVICE_SIZES.LG]: {
      chatBox: {
        marginTop: 20,
        position: "relative",
        right: 0,
        bottom: -0,
      },
    },
    [DEVICE_SIZES.MD]: {
      chatBox: {
        marginTop: 20,
        position: "relative",
        right: 0,
        bottom: -0,
      },
      messageListContainer: {
        minHeight: 350,
        maxHeight: 350,
        maxWidth: 350,
        minWidth: 300,
        marginVertical: "5%",
      },
    },
    [DEVICE_SIZES.SM]: {
      chatBox: {
        marginTop: 20,
        position: "relative",
        right: 0,
        bottom: -0,
      },
      messageListContainer: {
        minHeight: 350,
        maxHeight: 350,
        maxWidth: 350,
        minWidth: 250,
        marginVertical: "5%",
      },
      eventView: {
        marginTop: 5,
        marginBottom: 5,
        alignSelf: "center",
      },
    },
    [DEVICE_SIZES.XS]: {
      chatBox: {
        marginTop: 20,
        position: "relative",
        right: 0,
        bottom: -0,
      },
      messageListContainer: {
        minHeight: 350,
        maxHeight: 350,
        maxWidth: 350,
        minWidth: 200,
        marginVertical: "5%",
      },
      eventView: {
        marginTop: 5,
        marginBottom: 5,
        alignSelf: "center",
      },
    },
  }
);

export default withErrorBoundary(EventChatComponent);
